import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import {
  TextButton,
  TextButtonColor,
} from 'components/uikit/buttons/TextButton';
import { SearchInput } from 'components/uikit/inputs/SearchInput';
import ExpandIcon from 'assets/icons/expand-folders.svg?react';
import CollapseIcon from 'assets/icons/collapse-folders.svg?react';
import SelectModeIcon from 'assets/icons/select_mode_24.svg?react';
import { useState } from 'react';
import styles from './TreeControlPanel.module.scss';
import clsx from 'clsx';
import { useSelectModeContext } from 'pages/authorized/documents/documentList/selectMode/useSelectModeContext';
import { useModal } from 'components/uikit/modal/useModal';
import {
  ColumnSelectionDropdown,
  ColumnSelectionOption,
} from 'components/uikit/inputs/dropdown/ColumnSelectionDropdown';

interface TreeControlPanelProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  isExpandDisabled: boolean;
  onExpand: (isExpanded: boolean) => Promise<void>;
  additionalFilters?: React.ReactNode;
  styles?: {
    controlContainer?: string;
    actionsContainer?: string;
  };

  projectId?: number;
  tableColumns: ColumnSelectionOption[];

  enableSelectMode?: boolean; // TODO: remove after select mode is implemented for template list.
}

export const TreeControlPanel: React.FC<TreeControlPanelProps> = ({
  searchValue,
  onSearchChange,
  isExpandDisabled,
  additionalFilters,
  onExpand,
  styles: passedStyles,
  enableSelectMode,
  projectId,
  tableColumns,
}) => {
  const i18n = useScopedTranslation('Component.TreeControlPanel');
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  return (
    <div
      className={clsx(styles.controlContainer, passedStyles?.controlContainer)}
    >
      <div className={styles.filterContainer}>
        <SearchInput
          className={styles.searchInput}
          onChange={(e) => onSearchChange(e.currentTarget.value)}
          value={searchValue}
        />
        {additionalFilters}
      </div>
      <div
        className={clsx(
          styles.actionsContainer,
          passedStyles?.actionsContainer,
        )}
      >
        {enableSelectMode && (
          <SelectModeToggler title={i18n.t('select_mode')} />
        )}

        <TextButton
          testId="expand-all"
          className={styles.button}
          disabled={isExpandDisabled}
          color={TextButtonColor.Primary}
          icon={isExpanded ? <CollapseIcon /> : <ExpandIcon />}
          title={isExpanded ? i18n.t('collapse_all') : i18n.t('expand_all')}
          onClick={async () => {
            await onExpand(isExpanded);
            setIsExpanded((isExpanded) => !isExpanded);
          }}
        />

        <ColumnSelectionDropdown
          tableId={projectId ? projectId : 'template'}
          options={tableColumns}
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
          classes={{
            button: styles.button,
          }}
        />
      </div>
    </div>
  );
};

const SelectModeToggler = (props: { title: string }) => {
  const modals = useModal();
  const { isSelectMode, setIsSelectMode, store } = useSelectModeContext();
  const i18n = useScopedTranslation(
    'Component.TreeControlPanel.SelectModeToggler',
  );

  return (
    <TextButton
      testId="select-mode"
      className={styles.button}
      color={TextButtonColor.Primary}
      icon={<SelectModeIcon />}
      title={props.title}
      disabled={store['nodes'].size === 0}
      onClick={async () => {
        if (isSelectMode && store.getSelectedNodes().length !== 0) {
          const confirmed = await modals.showConfirm({
            title: i18n.t('confirm_exit_title'),
            text: i18n.t('confirm_exit_message'),
            okButtonText: i18n.t('confirm_exit_button'),
          });
          if (!confirmed) return;
        }

        setIsSelectMode((isSelectMode) => !isSelectMode);
      }}
    />
  );
};
